import { useState, useRef, useEffect } from "react";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Sparkles, ChevronDown, Image, Video, X, Loader2, GripVertical, ArrowLeft, ArrowRight } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBusiness } from "@/hooks/use-business";
import { useServices } from "@/hooks/use-services";
import { useServiceCategories } from "@/hooks/use-service-categories";
import { useLocations } from "@/hooks/use-locations";
import { logger } from "@/lib/logger";
import { useStaff } from "@/hooks/use-staff";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLocationContext } from "@/contexts/LocationContext";

// Currency helper
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    GHS: "₵",
    NGN: "₦",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatPrice = (amount: number | string | null | undefined, currency: string = "GHS"): string => {
  if (!amount) return `${getCurrencySymbol(currency)}0.00`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return `${getCurrencySymbol(currency)}${numAmount.toFixed(2)}`;
};

interface PresetService {
  name: string;
  description: string;
  duration: number;
  price: number;
}

interface ServiceCategory {
  name: string;
  services: PresetService[];
}

const serviceCatalog: ServiceCategory[] = [
  {
    name: "Hair Services",
    services: [
      { name: "Haircut", description: "Professional haircut tailored to your style preferences.", duration: 30, price: 50 },
      { name: "Hair Braiding", description: "Traditional or modern braiding styles with expert technique.", duration: 90, price: 80 },
      { name: "Hair Relaxing", description: "Chemical treatment to straighten and smooth your hair.", duration: 120, price: 100 },
      { name: "Dreadlocks Installation", description: "Professional dreadlocks creation and styling.", duration: 180, price: 150 },
      { name: "Weave Installation", description: "Full or partial weave installation with quality finishing.", duration: 150, price: 120 },
      { name: "Hair Coloring", description: "Full or partial hair coloring using high-quality dyes.", duration: 90, price: 85 },
      { name: "Keratin Treatment", description: "Smoothing treatment to reduce frizz and enhance shine.", duration: 120, price: 150 },
    ],
  },
  {
    name: "Barber Services",
    services: [
      { name: "Men's Haircut", description: "Precision haircut with professional styling.", duration: 30, price: 30 },
      { name: "Beard Trim", description: "Shaping and trimming of facial hair to your preference.", duration: 15, price: 20 },
      { name: "Hot Towel Shave", description: "Traditional hot towel shave for a smooth, clean finish.", duration: 30, price: 35 },
      { name: "Hair & Beard Combo", description: "Complete haircut and beard grooming service.", duration: 45, price: 45 },
      { name: "Head Shave", description: "Clean head shave with razor for a polished look.", duration: 30, price: 25 },
      { name: "Kids Haircut", description: "Specialized haircut service for children.", duration: 20, price: 20 },
    ],
  },
  {
    name: "Lash Services",
    services: [
      { name: "Classic Lashes (Same Length)", description: "Natural, almost invisible lash extensions with uniform length.", duration: 90, price: 120 },
      { name: "Classic Lashes (Cat Eye)", description: "Natural lash extensions with a cat eye effect (longer at outer corner).", duration: 90, price: 130 },
      { name: "Hybrid Lashes (Same Length)", description: "A blend of classic and volume lash extensions for a fuller look.", duration: 120, price: 150 },
      { name: "Hybrid Lashes (Cat Eye)", description: "A blend of classic and volume lash extensions with cat eye effect.", duration: 120, price: 160 },
      { name: "Volume Lashes (Same Length)", description: "Full, dramatic lash extensions of uniform length.", duration: 150, price: 180 },
      { name: "Volume Lashes (Cat Eye)", description: "Full, dramatic lash extensions with a cat eye effect.", duration: 150, price: 190 },
      { name: "Lash Lift", description: "A semi-permanent treatment that lifts and curls natural lashes.", duration: 60, price: 75 },
      { name: "Microbladed Brows", description: "Semi-permanent eyebrow enhancement using hair-like strokes.", duration: 120, price: 200 },
      { name: "Brow Tinting & Lamination", description: "Tinting to darken the brows and lamination to straighten and shape.", duration: 45, price: 55 },
      { name: "Lash Removal", description: "Safe removal of existing lash extensions.", duration: 30, price: 25 },
      { name: "Lash Refill", description: "Maintenance service to fill in any gaps in lash extensions.", duration: 60, price: 65 },
    ],
  },
  {
    name: "Nail Services",
    services: [
      { name: "Manicure", description: "Nail shaping, cuticle care, and polish application.", duration: 30, price: 25 },
      { name: "Pedicure", description: "Foot soak, exfoliation, nail care, and polish application.", duration: 45, price: 35 },
      { name: "Gel Nails", description: "Long-lasting gel nail application with your choice of color.", duration: 60, price: 45 },
      { name: "Nail Art", description: "Custom nail designs and artistic embellishments.", duration: 45, price: 30 },
      { name: "Acrylic Nails", description: "Durable acrylic nail extensions with optional designs.", duration: 75, price: 55 },
      { name: "Dip Powder", description: "Odorless, durable nail color application using dip powder.", duration: 60, price: 50 },
    ],
  },
  {
    name: "Beauty & Makeup",
    services: [
      { name: "Full Face Makeup", description: "Complete makeup application for any occasion.", duration: 60, price: 75 },
      { name: "Bridal Makeup", description: "Special occasion makeup with premium products and consultation.", duration: 90, price: 150 },
      { name: "Eyebrow Shaping", description: "Professional eyebrow threading or waxing with shaping.", duration: 20, price: 15 },
      { name: "Eyelash Extensions", description: "Individual lash application for fuller, longer lashes.", duration: 90, price: 120 },
      { name: "Makeup Lesson", description: "Personalized lesson on techniques, products, and application tips.", duration: 60, price: 80 },
      { name: "Special Effects Makeup", description: "Advanced makeup for cosplay, Halloween, or creative looks.", duration: 90, price: 100 },
    ],
  },
  {
    name: "Spa & Wellness",
    services: [
      { name: "Full Body Massage", description: "Relaxing massage therapy to relieve tension and stress.", duration: 60, price: 80 },
      { name: "Facial Treatment", description: "Deep cleansing facial with customized skincare products.", duration: 60, price: 70 },
      { name: "Body Scrub", description: "Exfoliating treatment to remove dead skin cells and rejuvenate skin.", duration: 45, price: 55 },
      { name: "Hot Stone Therapy", description: "Therapeutic massage using heated stones to relax muscles.", duration: 75, price: 95 },
      { name: "Couples Massage", description: "Relaxing massage session for two, in a shared room.", duration: 60, price: 150 },
      { name: "Aromatherapy Massage", description: "Massage using essential oils to enhance relaxation and stress relief.", duration: 60, price: 85 },
      { name: "Reflexology", description: "Focused pressure techniques on hands and feet to reduce tension.", duration: 45, price: 50 },
    ],
  },
  {
    name: "Clothing & Tailoring",
    services: [
      { name: "Custom Dress Making", description: "Made-to-measure dress creation from your choice of fabric.", duration: 180, price: 200 },
      { name: "Suit Tailoring", description: "Professional tailoring of suits for perfect fit and style.", duration: 180, price: 250 },
      { name: "Clothing Alterations", description: "Adjustments and repairs to existing garments.", duration: 60, price: 40 },
      { name: "Wedding Gown Fitting", description: "Personalized fittings and alterations for bridal gowns.", duration: 120, price: 150 },
    ],
  },
  {
    name: "General Practice",
    services: [
      { name: "General Consultation", description: "Comprehensive health assessment and medical consultation with a general practitioner.", duration: 30, price: 100 },
      { name: "Follow-up Consultation", description: "Follow-up visit to review treatment progress and adjust care plan.", duration: 15, price: 60 },
      { name: "Health Check-up", description: "Routine physical examination and health screening.", duration: 45, price: 120 },
      { name: "Chronic Disease Management", description: "Ongoing care and monitoring for chronic conditions like diabetes, hypertension.", duration: 30, price: 100 },
      { name: "Prescription Refill", description: "Review and renewal of existing prescriptions.", duration: 15, price: 50 },
      { name: "Minor Procedures", description: "Minor surgical procedures like wound suturing, mole removal.", duration: 30, price: 150 },
    ],
  },
  {
    name: "Cardiology",
    services: [
      { name: "Cardiac Consultation", description: "Specialist consultation for heart-related conditions and symptoms.", duration: 45, price: 200 },
      { name: "ECG (Electrocardiogram)", description: "Heart rhythm and electrical activity assessment.", duration: 15, price: 80 },
      { name: "Echocardiogram", description: "Ultrasound imaging of the heart to assess structure and function.", duration: 45, price: 250 },
      { name: "Stress Test", description: "Exercise stress test to evaluate heart function under physical activity.", duration: 60, price: 300 },
      { name: "Holter Monitor", description: "24-hour heart rhythm monitoring device placement and analysis.", duration: 30, price: 200 },
      { name: "Cardiac Follow-up", description: "Follow-up consultation to review test results and treatment plan.", duration: 30, price: 150 },
    ],
  },
  {
    name: "Pediatrics",
    services: [
      { name: "Child Health Check-up", description: "Comprehensive health examination for children and adolescents.", duration: 30, price: 100 },
      { name: "Newborn Consultation", description: "Postnatal care and health assessment for newborns.", duration: 30, price: 120 },
      { name: "Vaccination", description: "Routine childhood immunization and booster shots.", duration: 15, price: 50 },
      { name: "Growth & Development Assessment", description: "Evaluation of child's physical and developmental milestones.", duration: 30, price: 100 },
      { name: "Acute Illness Consultation", description: "Assessment and treatment of common childhood illnesses.", duration: 20, price: 80 },
      { name: "School Health Exam", description: "Medical examination required for school enrollment.", duration: 30, price: 90 },
    ],
  },
  {
    name: "Women's Health",
    services: [
      { name: "Gynecological Consultation", description: "Comprehensive women's health examination and consultation.", duration: 45, price: 150 },
      { name: "Prenatal Care", description: "Regular check-ups during pregnancy to monitor mother and baby's health.", duration: 30, price: 120 },
      { name: "Pap Smear", description: "Cervical cancer screening test.", duration: 15, price: 80 },
      { name: "Breast Examination", description: "Clinical breast exam and breast health assessment.", duration: 20, price: 100 },
      { name: "Family Planning Consultation", description: "Counseling on contraceptive options and family planning methods.", duration: 30, price: 100 },
      { name: "Menopause Management", description: "Consultation and treatment for menopause-related symptoms.", duration: 30, price: 120 },
    ],
  },
  {
    name: "Laboratory Services",
    services: [
      { name: "Blood Test (Basic)", description: "Complete blood count (CBC) and basic metabolic panel.", duration: 15, price: 60 },
      { name: "Blood Test (Comprehensive)", description: "Full blood workup including liver function, kidney function, lipid panel.", duration: 15, price: 120 },
      { name: "Diabetes Screening", description: "Blood glucose test and HbA1c for diabetes diagnosis and monitoring.", duration: 15, price: 80 },
      { name: "Cholesterol Panel", description: "Lipid profile to assess cardiovascular risk.", duration: 15, price: 70 },
      { name: "Urine Analysis", description: "Urine test for infection, kidney function, and other conditions.", duration: 10, price: 40 },
      { name: "STI Screening", description: "Testing for sexually transmitted infections.", duration: 15, price: 100 },
      { name: "Pregnancy Test", description: "Blood or urine test to confirm pregnancy.", duration: 10, price: 30 },
    ],
  },
  {
    name: "Vaccinations",
    services: [
      { name: "COVID-19 Vaccine", description: "COVID-19 vaccination and booster shots.", duration: 15, price: 0 },
      { name: "Flu Vaccine", description: "Annual influenza vaccination.", duration: 10, price: 40 },
      { name: "Hepatitis B Vaccine", description: "Hepatitis B immunization series.", duration: 10, price: 50 },
      { name: "Tetanus Shot", description: "Tetanus, diphtheria, and pertussis (Tdap) vaccination.", duration: 10, price: 45 },
      { name: "Travel Vaccinations", description: "Required vaccinations for international travel.", duration: 20, price: 80 },
      { name: "HPV Vaccine", description: "Human papillomavirus vaccination for cervical cancer prevention.", duration: 10, price: 100 },
    ],
  },
  {
    name: "Mental Health",
    services: [
      { name: "Psychiatric Consultation", description: "Initial assessment and diagnosis of mental health conditions.", duration: 60, price: 200 },
      { name: "Therapy Session", description: "Individual counseling and psychotherapy session.", duration: 50, price: 150 },
      { name: "Follow-up Consultation", description: "Regular follow-up visit to monitor treatment progress.", duration: 30, price: 100 },
      { name: "Medication Review", description: "Review and adjustment of psychiatric medications.", duration: 30, price: 120 },
      { name: "Crisis Intervention", description: "Immediate support and intervention for mental health emergencies.", duration: 60, price: 200 },
    ],
  },
  {
    name: "Dermatology",
    services: [
      { name: "Skin Consultation", description: "Assessment and treatment of skin conditions, rashes, and disorders.", duration: 30, price: 150 },
      { name: "Acne Treatment", description: "Consultation and treatment plan for acne management.", duration: 30, price: 120 },
      { name: "Mole Removal", description: "Surgical removal of benign or suspicious moles.", duration: 30, price: 200 },
      { name: "Skin Biopsy", description: "Tissue sample collection for laboratory analysis.", duration: 20, price: 150 },
      { name: "Eczema Treatment", description: "Management and treatment of eczema and dermatitis.", duration: 30, price: 120 },
    ],
  },
  {
    name: "Orthopedics",
    services: [
      { name: "Orthopedic Consultation", description: "Assessment of bone, joint, and muscle conditions.", duration: 45, price: 200 },
      { name: "X-Ray", description: "Diagnostic imaging for fractures, joint issues, and bone conditions.", duration: 20, price: 100 },
      { name: "Sports Injury Assessment", description: "Evaluation and treatment plan for sports-related injuries.", duration: 30, price: 150 },
      { name: "Joint Injection", description: "Therapeutic injection for joint pain and inflammation.", duration: 15, price: 120 },
      { name: "Physical Therapy Referral", description: "Assessment and referral for physical therapy services.", duration: 30, price: 100 },
    ],
  },
];

const durationPresets = [
  { label: "0", value: 0 },
  { label: "30m", value: 30 },
  { label: "1h", value: 60 },
  { label: "2h", value: 120 },
  { label: "3h", value: 180 },
];

export default function Services() {
  const { business, loading: businessLoading } = useBusiness();
  const { services, loading: servicesLoading, createService, updateService, archiveService } = useServices();
  const { categories, loading: categoriesLoading, createCategory, updateCategory, deleteCategory } = useServiceCategories();
  const { locations, loading: locationsLoading } = useLocations();
  const { staff, loading: staffLoading } = useStaff();
  const { toast } = useToast();
  const { selectedLocationId } = useLocationContext();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [serviceLocations, setServiceLocations] = useState<Record<string, string[]>>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(serviceCatalog[0].name);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formDuration, setFormDuration] = useState(30);
  const [formBufferBefore, setFormBufferBefore] = useState(0);
  const [formBufferAfter, setFormBufferAfter] = useState(0);
  const [showBufferFields, setShowBufferFields] = useState(false);
  const [formDeposit, setFormDeposit] = useState("");
  const [formAutoConfirm, setFormAutoConfirm] = useState(true);
  const [formActive, setFormActive] = useState(true);
  const [formPosition, setFormPosition] = useState("1");
  const [formCategoryId, setFormCategoryId] = useState<string>("none");
  const [formMedia, setFormMedia] = useState<{ id: string; file: File; preview: string; type: 'image' | 'video' }[]>([]);
  const [formAssignedStaff, setFormAssignedStaff] = useState<string[]>([]);
  const [formSelectedLocations, setFormSelectedLocations] = useState<string[]>([]);
  const [formAddons, setFormAddons] = useState<Array<{ id?: string; name: string; price: string; maxQuantity: string }>>([]);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Category form state (manager dialog)
  const [catEditingId, setCatEditingId] = useState<string | null>(null);
  const [catName, setCatName] = useState("");
  const [catPosition, setCatPosition] = useState("1");
  const [catActive, setCatActive] = useState(true);
  const [catAllowMultiple, setCatAllowMultiple] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch service locations mapping
  useEffect(() => {
    const fetchServiceLocations = async () => {
      if (!business) return;
      
      const { data, error } = await (supabase as any)
        .from("service_locations")
        .select("service_id, location_id");
      
      if (error) {
        logger.error("Failed to fetch service locations", error as Error);
        return;
      }
      
      const mapping: Record<string, string[]> = {};
      (data || [])?.forEach((sl: any) => {
        if (!mapping[sl.service_id]) {
          mapping[sl.service_id] = [];
        }
        mapping[sl.service_id].push(sl.location_id);
      });
      
      setServiceLocations(mapping);
    };
    
    fetchServiceLocations();
  }, [business]);

  const filteredServices = services.filter((service) => {
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Location filter: check if service is assigned to the selected location
    // If service has no location assignments, it's available at all locations
    const matchesLocation = !selectedLocationId || (() => {
      const serviceLocationIds = serviceLocations[service.id] || [];
      // If service has no location assignments, it's available at all locations
      if (serviceLocationIds.length === 0) return true;
      // Otherwise, check if it's assigned to the selected location
      return serviceLocationIds.includes(selectedLocationId);
    })();
    
    return matchesSearch && matchesLocation;
  });

  const selectedCategoryServices = serviceCatalog.find(c => c.name === selectedCategory)?.services || [];

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormPrice("");
    setFormDuration(30);
    setFormBufferBefore(0);
    setFormBufferAfter(0);
    setShowBufferFields(false);
    setFormDeposit("");
    setFormAutoConfirm(true);
    setFormActive(true);
    setFormPosition("1");
    setFormCategoryId("none");
    setFormMedia([]);
    setDragFromIndex(null);
    setDragOverIndex(null);
    setFormAssignedStaff([]);
    setFormSelectedLocations([]);
    setFormAddons([]);
    setEditingServiceId(null);
  };

  const resetCategoryForm = () => {
    setCatEditingId(null);
    setCatName("");
    setCatPosition("1");
    setCatActive(true);
    setCatAllowMultiple(false);
  };

  const loadServiceAddons = async (serviceId: string) => {
    try {
      const { data, error } = await supabase
        .from("service_addons")
        .select("id, name, price_amount, max_quantity")
        .eq("service_id", serviceId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      setFormAddons(
        (data || []).map((a: any) => ({
          id: a.id,
          name: a.name || "",
          price: (a.price_amount ?? 0).toString(),
          maxQuantity: String(Math.max(1, Number(a.max_quantity ?? 1))),
        }))
      );
    } catch {
      setFormAddons([]);
    }
  };

  const handleEditService = async (service: typeof services[0]) => {
    setEditingServiceId(service.id);
    setFormName(service.name);
    setFormDescription(service.description || "");
    setFormPrice(service.price_amount?.toString() || "0");
    setFormDuration(service.duration_minutes);
    setFormBufferBefore(service.buffer_before_minutes || 0);
    setFormBufferAfter(service.buffer_after_minutes || 0);
    setShowBufferFields(!!(service.buffer_before_minutes || service.buffer_after_minutes));
    setFormActive(service.active ?? true);
    setFormDeposit(service.deposit_amount?.toString() || "");
    setFormAssignedStaff(service.assignedStaffIds || []);
    setFormSelectedLocations(service.assignedLocationIds || []);
    setFormCategoryId((service as any)?.category_id ? String((service as any).category_id) : "none");
    
    // Load existing media items
    const mediaItems = (service as any)?.media_items;
    if (Array.isArray(mediaItems) && mediaItems.length > 0) {
      // For existing media, we can't create File objects, so we'll store them as preview URLs
      // The form will show them, but they won't be re-uploaded (they're already in storage)
      setFormMedia(mediaItems.map((item: { url: string; type: 'image' | 'video' }, idx: number) => ({
        id: `existing:${service.id}:${idx}:${item.url}`,
        file: new File([], item.url.split('/').pop() || 'existing-media'), // Dummy file for existing media
        preview: item.url,
        type: item.type,
      })));
    } else if (service.image_url) {
      // Fallback to image_url for backward compatibility
      const url = service.image_url.toLowerCase();
      const isVideo = url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.endsWith('.mkv');
      setFormMedia([{
        id: `legacy:${service.id}:${service.image_url}`,
        file: new File([], service.image_url.split('/').pop() || 'existing-media'),
        preview: service.image_url,
        type: isVideo ? 'video' : 'image',
      }]);
    } else {
      setFormMedia([]);
    }
    
    await loadServiceAddons(service.id);
    setIsDialogOpen(true);
  };

  const toggleStaffAssignment = (staffId: string) => {
    setFormAssignedStaff(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    const newMedia: { id: string; file: File; preview: string; type: 'image' | 'video' }[] = [];
    
    Array.from(files).forEach((file) => {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      
      if (isVideo || isImage) {
        const preview = URL.createObjectURL(file);
        newMedia.push({
          id: `new:${Date.now()}:${Math.random().toString(16).slice(2)}`,
          file,
          preview,
          type: isVideo ? 'video' : 'image',
        });
      }
    });
    
    setFormMedia([...formMedia, ...newMedia]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    const newMedia = [...formMedia];
    URL.revokeObjectURL(newMedia[index].preview);
    newMedia.splice(index, 1);
    setFormMedia(newMedia);
  };

  const moveMedia = (from: number, to: number) => {
    setFormMedia((prev) => {
      if (from === to) return prev;
      if (from < 0 || to < 0) return prev;
      if (from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handlePresetClick = (preset: PresetService) => {
    setFormName(preset.name);
    setFormDescription(preset.description);
    setFormDuration(preset.duration);
    setFormPrice(preset.price.toString());
    setIsDialogOpen(true);
  };

  const saveServiceAddons = async (serviceId: string) => {
    if (!business) return;

    const cleaned = formAddons
      .map((a) => ({
        name: a.name.trim(),
        price_amount: parseFloat(a.price) || 0,
        max_quantity: Math.max(1, parseInt(String(a.maxQuantity || "1"), 10) || 1),
      }))
      .filter((a) => a.name.length > 0);

    // Guard: add-on names must be unique (case-insensitive) to avoid silent overwrites.
    const countsByLower = new Map<string, number>();
    for (const a of cleaned) {
      const key = a.name.toLowerCase();
      countsByLower.set(key, (countsByLower.get(key) || 0) + 1);
    }
    const dupes = Array.from(countsByLower.entries())
      .filter(([, n]) => n > 1)
      .map(([k]) => k);
    if (dupes.length > 0) {
      toast({
        title: "Duplicate add-on names",
        description: `Each add-on name must be unique. Please rename duplicates: ${dupes.join(", ")}.`,
        variant: "destructive",
      });
      throw new Error("Duplicate add-on names (case-insensitive) are not allowed");
    }

    // Fetch existing addons to compare
    const { data: existingAddons } = await supabase
      .from("service_addons")
      .select("id, name, price_amount, max_quantity")
      .eq("service_id", serviceId);

    // Check which addons are referenced by appointments (can't delete these)
    const existingIds = (existingAddons || []).map(a => a.id);
    const referencedIds = new Set<string>();
    if (existingIds.length > 0) {
      const { data: referencedAddons } = await supabase
        .from("appointment_addons")
        .select("addon_id")
        .in("addon_id", existingIds);
      (referencedAddons || []).forEach(a => referencedIds.add(a.addon_id));

      // Food orders add-ons (optional module) also prevent deleting referenced add-ons
      try {
        const { data: referencedFood } = await (supabase as any)
          .from("food_order_item_addons")
          .select("addon_id")
          .in("addon_id", existingIds);
        (referencedFood || []).forEach((a: any) => referencedIds.add(String(a.addon_id)));
      } catch {
        // If the migration hasn't been applied yet, ignore.
      }
    }

    // Find addons to delete (exist in DB but not in form, and not referenced)
    const formAddonNames = new Set(cleaned.map(a => a.name.toLowerCase()));
    const toDelete = (existingAddons || [])
      .filter(existing => 
        !formAddonNames.has(existing.name.toLowerCase()) && 
        !referencedIds.has(existing.id)
      )
      .map(a => a.id);

    // Delete only unreferenced addons that are no longer in the form
    if (toDelete.length > 0) {
      const { error: delErr } = await supabase
        .from("service_addons")
        .delete()
        .in("id", toDelete);
      if (delErr) {
        logger.warn("Some addons couldn't be deleted", { error: String(delErr), serviceId });
      }
    }

    // Update existing addons (price/limit) by name match (case-insensitive)
    const byName = new Map<string, { price_amount: number; max_quantity: number }>();
    for (const a of cleaned) {
      byName.set(a.name.toLowerCase(), { price_amount: a.price_amount, max_quantity: a.max_quantity });
    }

    const updates = (existingAddons || [])
      .map((ex: any) => {
        const next = byName.get(String(ex.name || "").toLowerCase());
        if (!next) return null;
        const curPrice = Number(ex.price_amount ?? 0);
        const curMax = Math.max(1, Number(ex.max_quantity ?? 1));
        if (curPrice === next.price_amount && curMax === next.max_quantity) return null;
        return { id: ex.id, ...next };
      })
      .filter(Boolean) as Array<{ id: string; price_amount: number; max_quantity: number }>;

    if (updates.length > 0) {
      await Promise.all(
        updates.map((u) =>
          supabase
            .from("service_addons")
            .update({ price_amount: u.price_amount, max_quantity: u.max_quantity } as any)
            .eq("id", u.id),
        ),
      );
    }

    // Insert new addons (that don't already exist)
    const existingNames = new Set((existingAddons || []).map(a => a.name.toLowerCase()));
    const toInsert = cleaned.filter(a => !existingNames.has(a.name.toLowerCase()));

    if (toInsert.length > 0) {
    const { error: insErr } = await supabase.from("service_addons").insert(
        toInsert.map((a) => ({
        business_id: business.id,
        service_id: serviceId,
        name: a.name,
        price_amount: a.price_amount,
        max_quantity: a.max_quantity,
        currency: business.currency || "GHS",
        active: true,
      })) as any
    );
    if (insErr) throw insErr;
    }
  };

  const handleAddService = async () => {
    if (!formName.trim()) return;
    
    setIsSaving(true);
    try {
      let imageUrl: string | undefined = undefined;
      let mediaItems: Array<{ url: string; type: 'image' | 'video' }> = [];
      
      // Process media items: upload new ones, keep existing ones
      if (formMedia.length > 0 && business) {
        const processedMedia = await Promise.all(
          formMedia.map(async (media) => {
            // Check if this is an existing media item (has a preview URL that's already a full URL)
            // New uploads will have blob: URLs as preview, existing ones will have https:// URLs
            const isExisting = media.preview.startsWith('http://') || media.preview.startsWith('https://');
            
            if (isExisting) {
              // This is existing media, use the preview URL directly
              return {
                url: media.preview,
                type: media.type,
              };
            } else {
              // This is new media, upload it
              const fileExt = media.file.name.split('.').pop();
              const fileName = `${business.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
              
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('service-media')
                .upload(fileName, media.file, {
                  cacheControl: '3600',
                  upsert: false
                });
              
              if (uploadError) {
                logger.error('Service media upload error', uploadError as Error, { businessId: business.id, fileName });
                throw uploadError;
              }
              
              // Get public URL
              const { data: urlData } = supabase.storage
                .from('service-media')
                .getPublicUrl(uploadData.path);
              
              return {
                url: urlData.publicUrl,
                type: media.type,
              };
            }
          })
        );

        try {
          mediaItems = processedMedia;
          // Set image_url to first media item for backward compatibility
          if (mediaItems.length > 0) {
            imageUrl = mediaItems[0].url;
          }
        } catch (uploadError) {
          logger.error('Service media upload error', uploadError as Error, { businessId: business.id });
          toast({
            title: "Upload failed",
            description: "Failed to upload some media. Service will be saved with available media.",
            variant: "destructive",
          });
          // Continue with whatever media was successfully processed
        }
      }
      
      const serviceData = {
        name: formName,
        description: formDescription,
        duration_minutes: formDuration,
        buffer_before_minutes: formBufferBefore || null,
        buffer_after_minutes: formBufferAfter || null,
        price_amount: parseFloat(formPrice) || 0,
        currency: business?.currency || 'GHS',
        active: formActive,
        deposit_amount: formDeposit ? parseFloat(formDeposit) : null,
        requires_deposit: !!formDeposit,
        image_url: imageUrl, // Keep for backward compatibility
        media_items: mediaItems.length > 0 ? mediaItems : null, // New field for multiple media
        category_id: formCategoryId === "none" ? null : formCategoryId,
      };
      
      if (editingServiceId) {
        await updateService(editingServiceId, serviceData as any, formAssignedStaff, formSelectedLocations);
        await saveServiceAddons(editingServiceId);
      } else {
        const created = await createService(serviceData as any, formAssignedStaff, formSelectedLocations);
        if (created?.id) {
          await saveServiceAddons(created.id);
        }
      }
      
      // Show success toast
      toast({
        title: "Success",
        description: editingServiceId ? "Service updated successfully" : "Service created successfully",
        variant: "success",
      });
      
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save service",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveService = async (serviceId: string) => {
    try {
      await archiveService(serviceId);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to archive service",
        variant: "destructive",
      });
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const isLoading = businessLoading || servicesLoading || staffLoading;

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Services"
        description="Manage the services you offer to your customers"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="pill" onClick={() => { resetCategoryForm(); setIsCategoriesOpen(true); }}>
              Categories
            </Button>
          <Button size="pill" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Service
          </Button>
          </div>
        }
      />

      {/* Quick Add Section */}
      <div className="mb-6 rounded-xl border border-border bg-card p-5 space-y-4">
        <Collapsible open={isQuickAddOpen} onOpenChange={setIsQuickAddOpen}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Click to auto add popular services</span>
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${isQuickAddOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {serviceCatalog.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    selectedCategory === category.name
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:border-primary/50"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Selected Category Services */}
            <div className="rounded-lg border border-border bg-background p-4">
              <h4 className="text-sm font-medium text-foreground mb-3">{selectedCategory}</h4>
              <div className="grid md:grid-cols-2 gap-2">
                {selectedCategoryServices.map((service) => (
                  <button
                    key={service.name}
                    onClick={() => handlePresetClick(service)}
                    className="text-left p-3 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
                  >
                    <p className="font-medium text-foreground text-sm">{service.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {service.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Collapsible Full Catalog */}
            <Collapsible open={isCatalogOpen} onOpenChange={setIsCatalogOpen}>
              <CollapsibleTrigger asChild>
                <button className="w-full flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 text-sm text-primary hover:bg-muted/50 transition-colors">
                  <span>Explore our complete service catalog</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isCatalogOpen ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                <div className="rounded-lg border border-border bg-background p-4 max-h-[400px] overflow-y-auto">
                  {serviceCatalog.map((category) => (
                    <div key={category.name} className="mb-6 last:mb-0">
                      <h4 className="text-sm font-medium text-primary mb-3">{category.name}</h4>
                      <div className="grid md:grid-cols-2 gap-2">
                        {category.services.map((service) => (
                          <button
                            key={service.name}
                            onClick={() => handlePresetClick(service)}
                            className="text-left p-3 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
                          >
                            <p className="font-medium text-foreground text-sm">{service.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {service.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Info Banner */}
      <div className="mb-6 flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/20 px-4 py-3 text-sm text-primary">
        <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>Services you add here will be available for booking. Remember to assign staff members who can perform each service in the Employees section.</span>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Services Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredServices.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className="bg-card rounded-xl border border-border shadow-soft p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-foreground">{service.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {service.description}
                  </p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEditService(service)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-destructive"
                      onClick={() => handleArchiveService(service.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Archive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">
                    {service.duration_minutes} min
                  </span>
                  <span className="font-medium text-foreground">
                    {formatPrice(service.price_amount, service.currency || business?.currency || "GHS")}
                  </span>
                </div>
                <StatusBadge status={service.active ? "active" : "inactive"} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-soft">
          <EmptyState
            title="No services found"
            description="Add your first service to start accepting bookings"
            action={{
              label: "Add Service",
              onClick: () => { resetForm(); setIsDialogOpen(true); },
            }}
          />
        </div>
      )}

      {/* Add/Edit Service Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] max-h-[82vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{editingServiceId ? "Edit Service" : "Add Service"}</DialogTitle>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <ChevronDown className="h-3 w-3" />
              Scroll for more options
            </p>
          </DialogHeader>
          
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-5">
              {/* Service Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Service Name</Label>
                <Input 
                  id="name" 
                  placeholder="e.g., Haircut, Massage, Consultation" 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">Price ({business?.currency || "GHS"})</Label>
                <Input 
                  id="price" 
                  type="number" 
                  placeholder="e.g., 50" 
                  value={formPrice}
                  onChange={(e) => setFormPrice(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the price as a number only (e.g., 50 or 49.99). No need to include currency symbols.
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  placeholder="Describe what this service includes" 
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Category (optional)</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { resetCategoryForm(); setIsCategoriesOpen(true); }}>
                    Manage
                  </Button>
                </div>
                <Select value={formCategoryId} onValueChange={setFormCategoryId} disabled={categoriesLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder={categoriesLoading ? "Loading categories..." : "Select a category"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {categories.filter((c) => c.active).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Categories help customers filter services on your booking page.
                </p>
              </div>

              {/* Locations — only show when the account has multiple locations */}
              {locations.length > 1 && (
                <div className="space-y-2">
                  <Label>Available at Locations (optional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Select which locations offer this service. Leave empty to make it available at all locations.
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                    {locations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formSelectedLocations.includes(location.id)}
                          onChange={() => {
                            if (formSelectedLocations.includes(location.id)) {
                              setFormSelectedLocations(formSelectedLocations.filter(id => id !== location.id));
                            } else {
                              setFormSelectedLocations([...formSelectedLocations, location.id]);
                            }
                          }}
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium">{location.name}</span>
                        {location.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </label>
                    ))}
                  </div>
                  {formSelectedLocations.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">
                      No locations selected - service will be available at all locations
                    </p>
                  )}
                </div>
              )}

              {/* Add-ons */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-sm">Add-ons (optional)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormAddons([...formAddons, { name: "", price: "", maxQuantity: "1" }])}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add add-on
                  </Button>
                </div>

                {formAddons.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Add extras customers can optionally select (e.g. “Nail art”).
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formAddons.map((addon, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          placeholder="Add-on name"
                          value={addon.name}
                          onChange={(e) => {
                            const next = [...formAddons];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setFormAddons(next);
                          }}
                        />
                        <Input
                          placeholder="Price"
                          inputMode="decimal"
                          className="w-28"
                          value={addon.price}
                          onChange={(e) => {
                            const next = [...formAddons];
                            next[idx] = { ...next[idx], price: e.target.value };
                            setFormAddons(next);
                          }}
                        />
                        <Input
                          placeholder="Limit"
                          inputMode="numeric"
                          className="w-20"
                          value={addon.maxQuantity}
                          onChange={(e) => {
                            const next = [...formAddons];
                            const raw = e.target.value;
                            // allow empty while typing; normalize on save
                            next[idx] = { ...next[idx], maxQuantity: raw };
                            setFormAddons(next);
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const next = [...formAddons];
                            next.splice(idx, 1);
                            setFormAddons(next);
                          }}
                          aria-label="Remove add-on"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Media Upload */}
              <div className="space-y-3">
                <Label>Photos & Videos</Label>
                <p className="text-xs text-muted-foreground">
                  Add photos or short videos to showcase this service on your booking page.
                </p>
                {formMedia.length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    Drag to reorder. The first item will be used as the cover image.
                  </p>
                )}
                
                {/* Media Preview */}
                {formMedia.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formMedia.map((media, index) => (
                      <div
                        key={media.id}
                        className={`relative group rounded-lg ${
                          dragOverIndex === index && dragFromIndex !== index ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""
                        }`}
                        draggable={formMedia.length > 1}
                        onDragStart={(e) => {
                          if (formMedia.length <= 1) return;
                          setDragFromIndex(index);
                          setDragOverIndex(index);
                          try {
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", String(index));
                          } catch {
                            // ignore
                          }
                        }}
                        onDragOver={(e) => {
                          if (dragFromIndex === null) return;
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverIndex(index);
                          try {
                            e.dataTransfer.dropEffect = "move";
                          } catch {
                            // ignore
                          }
                        }}
                        onDrop={(e) => {
                          if (dragFromIndex === null) return;
                          e.preventDefault();
                          e.stopPropagation();
                          moveMedia(dragFromIndex, index);
                          setDragFromIndex(null);
                          setDragOverIndex(null);
                        }}
                        onDragEnd={() => {
                          setDragFromIndex(null);
                          setDragOverIndex(null);
                        }}
                        title={formMedia.length > 1 ? "Drag to reorder" : undefined}
                      >
                        {media.type === 'image' ? (
                          <img 
                            src={media.preview} 
                            alt={`Service media ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-border cursor-grab active:cursor-grabbing"
                          />
                        ) : (
                          <div className="relative w-20 h-20 rounded-lg border border-border overflow-hidden bg-muted cursor-grab active:cursor-grabbing">
                            <video 
                              src={media.preview}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Video className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        )}

                        {formMedia.length > 1 && (
                          <div className="absolute -top-2 -left-2 bg-background/90 backdrop-blur border border-border rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <GripVertical className="h-3 w-3 text-muted-foreground" />
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>

                        {/* Mobile-friendly reorder fallback */}
                        {formMedia.length > 1 && (
                          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon"
                              className="h-6 w-6 rounded-full shadow-sm"
                              onClick={() => moveMedia(index, Math.max(0, index - 1))}
                              disabled={index === 0}
                              aria-label="Move left"
                            >
                              <ArrowLeft className="h-3 w-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon"
                              className="h-6 w-6 rounded-full shadow-sm"
                              onClick={() => moveMedia(index, Math.min(formMedia.length - 1, index + 1))}
                              disabled={index === formMedia.length - 1}
                              aria-label="Move right"
                            >
                              <ArrowRight className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image className="mr-2 h-4 w-4" />
                  Add Photos or Videos
                </Button>
              </div>

              {/* Duration */}
              <div className="space-y-3">
                <Label>Duration</Label>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {formDuration === 0 ? "Custom" : formatDuration(formDuration)}
                  </span>
                  <div className="flex gap-2">
                    {durationPresets.map((preset) => (
                      <Button
                        key={preset.value}
                        type="button"
                        size="sm"
                        variant={formDuration === preset.value ? "default" : "outline"}
                        className="h-8 px-3"
                        onClick={() => setFormDuration(preset.value)}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <Slider
                  value={[formDuration]}
                  onValueChange={(value) => setFormDuration(value[0])}
                  min={0}
                  max={1440}
                  step={15}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0</span>
                  <span>12h</span>
                  <span>24h</span>
                </div>
              </div>

              {/* Buffer Times — collapsed by default, expandable */}
              <div className="space-y-3">
                {!showBufferFields ? (
                  <button
                    type="button"
                    onClick={() => setShowBufferFields(true)}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add buffer time
                  </button>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <Label>Buffer Times</Label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowBufferFields(false);
                          setFormBufferBefore(0);
                          setFormBufferAfter(0);
                        }}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add time before and after the service for setup and cleanup. This prevents back-to-back bookings.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="buffer-before" className="text-xs">Before (minutes)</Label>
                        <Input
                          id="buffer-before"
                          type="number"
                          min="0"
                          max="120"
                          step="5"
                          placeholder="0"
                          value={formBufferBefore}
                          onChange={(e) => setFormBufferBefore(parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="buffer-after" className="text-xs">After (minutes)</Label>
                        <Input
                          id="buffer-after"
                          type="number"
                          min="0"
                          max="120"
                          step="5"
                          placeholder="0"
                          value={formBufferAfter}
                          onChange={(e) => setFormBufferAfter(parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Deposit */}
              <div className="space-y-2">
                <Label htmlFor="deposit">Non-refundable Deposit ({business?.currency || "GHS"})</Label>
                <Input 
                  id="deposit" 
                  type="number" 
                  placeholder="e.g., 10" 
                  value={formDeposit}
                  onChange={(e) => setFormDeposit(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  The deposit amount cannot exceed the service price and will count towards the total service price for the booking.
                </p>
              </div>

              {/* Booking Confirmation */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="autoConfirm">Booking Confirmation</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Automatic</span>
                    <Switch 
                      id="autoConfirm" 
                      checked={formAutoConfirm}
                      onCheckedChange={setFormAutoConfirm}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  When enabled, new appointments will be automatically confirmed. When disabled, appointments will require manual confirmation.
                </p>
              </div>

              {/* Service Status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="active">Service Status</Label>
                    <Badge variant={formActive ? "default" : "secondary"} className="text-xs">
                      {formActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Available</span>
                    <Switch 
                      id="active" 
                      checked={formActive}
                      onCheckedChange={setFormActive}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  When disabled, this service will not be available for clients to book. Existing bookings will not be affected.
                </p>
              </div>

              {/* Position */}
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Select value={formPosition} onValueChange={setFormPosition}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => (
                      <SelectItem key={pos} value={pos.toString()}>
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Assign Staff */}
              {staff.length > 0 && (
                <div className="space-y-3">
                  <Label>Assign Staff</Label>
                  <p className="text-xs text-muted-foreground">
                    Select which staff members can perform this service.
                  </p>
                  <div className="space-y-2">
                    {staff.filter(s => s.active).map((member) => (
                      <label
                        key={member.id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formAssignedStaff.includes(member.id)}
                          onChange={() => toggleStaffAssignment(member.id)}
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium">{member.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 rounded-lg">
              Cancel
            </Button>
            <Button onClick={handleAddService} className="flex-1 rounded-lg" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingServiceId ? "Save Changes" : "Add Service"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog
        open={isCategoriesOpen}
        onOpenChange={(open) => {
          setIsCategoriesOpen(open);
          if (!open) resetCategoryForm();
        }}
      >
        <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)] max-h-[82vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Service Categories</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Create categories like “Spa”, “Nails”, “Hair”, “Piercings” to keep your booking list clean.
            </p>
          </DialogHeader>

          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0 space-y-4">
            <div className="rounded-xl border border-border bg-background p-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">Name</Label>
                  <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="e.g. Spa" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Order</Label>
                  <Input value={catPosition} onChange={(e) => setCatPosition(e.target.value)} inputMode="numeric" />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={catActive} onCheckedChange={setCatActive} />
                  <span className="text-sm text-muted-foreground">Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={catAllowMultiple} onCheckedChange={setCatAllowMultiple} />
                  <span className="text-sm text-muted-foreground">Allow multi-select</span>
                </div>
                <Button
                  type="button"
                  onClick={async () => {
                    if (!catName.trim()) {
                      toast({ title: "Name required", variant: "destructive" });
                      return;
                    }
                    try {
                      const payload = {
                        name: catName.trim(),
                        position: Math.max(1, parseInt(catPosition || "1", 10) || 1),
                        active: !!catActive,
                        allow_multiple_services: !!catAllowMultiple,
                      };
                      if (catEditingId) {
                        await updateCategory(catEditingId, payload as any);
                      } else {
                        await createCategory(payload as any);
                      }
                      resetCategoryForm();
                    } catch (e: any) {
                      toast({
                        title: "Failed to save category",
                        description: e?.message || "Try again.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  {catEditingId ? "Save" : "Add"}
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <p className="font-medium text-sm">Your categories</p>
                <p className="text-xs text-muted-foreground">{categories.length} total</p>
              </div>
              <div className="p-2">
                {categories.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">No categories yet.</div>
                ) : (
                  <div className="space-y-1">
                    {categories.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-foreground truncate">{c.name}</p>
                            <Badge variant={c.active ? "default" : "secondary"} className="text-xs">
                              {c.active ? "Active" : "Hidden"}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">Order: {c.position}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setCatEditingId(c.id);
                                setCatName(c.name);
                                setCatPosition(String(c.position || 1));
                                setCatActive(!!c.active);
                                setCatAllowMultiple(!!(c as any).allow_multiple_services);
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  await updateCategory(c.id, { active: !c.active } as any);
                                } catch (e: any) {
                                  toast({
                                    title: "Failed to update",
                                    description: e?.message || "Try again.",
                                    variant: "destructive",
                                  });
                                }
                              }}
                            >
                              {c.active ? "Hide" : "Show"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={async () => {
                                if (!confirm(`Delete category "${c.name}"? Services will become uncategorized.`)) return;
                                try {
                                  await deleteCategory(c.id);
                                  if (formCategoryId === c.id) setFormCategoryId("none");
                                } catch (e: any) {
                                  toast({
                                    title: "Failed to delete",
                                    description: e?.message || "Try again.",
                                    variant: "destructive",
                                  });
                                }
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setIsCategoriesOpen(false)} className="flex-1 rounded-lg">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
